import { useMemo, useState } from 'react'
import { Alert, Button, Form, Input, Modal, Select, Space, Switch, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { seededName } from '@/shared/lib/seededName'
import { PageHeader } from '@/shared/ui/PageHeader'
import { RowActions } from '@/shared/ui/table/RowActions'
import { fetchCostCenters } from '@/features/finance/financeApi'
import {
  EXPENSE_CATEGORY_NAME_MAX,
  createExpenseCategory,
  fetchExpenseCategories,
  updateExpenseCategory,
  type ExpenseCategory,
} from '@/features/finance/expenseCategoryApi'

interface CategoryForm {
  name: string
  branchIds: string[]
  active: boolean
}

/** Kassa xarajati turlari: nom va qaysi filial kassasida tanlanishi (ADR-050) */
export function ExpenseCategoriesPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [form] = Form.useForm<CategoryForm>()
  const [editing, setEditing] = useState<ExpenseCategory | 'new' | null>(null)
  const current = editing !== null && editing !== 'new' ? editing : null

  const categories = useQuery({
    queryKey: queryKeys.finance.expenseCategories,
    queryFn: ({ signal }) => fetchExpenseCategories(signal),
  })
  const branches = useQuery({
    queryKey: queryKeys.finance.costCenters,
    queryFn: ({ signal }) => fetchCostCenters(signal),
    staleTime: 5 * 60_000,
  })
  const branchName = useMemo(() => {
    const names = new Map((branches.data ?? []).map((branch) => [branch.id, branch.displayName]))
    return (id: string) => names.get(id) ?? t('expenseCategories.unknownBranch')
  }, [branches.data, t])

  const save = useMutation({
    mutationFn: (values: CategoryForm) => {
      const draft = { name: values.name.trim(), branchIds: values.branchIds, active: values.active }
      return current ? updateExpenseCategory(current.id, draft) : createExpenseCategory(draft)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.expenseCategories })
      void queryClient.invalidateQueries({ queryKey: ['cashdesk', 'expenseAccounts'] })
      setEditing(null)
    },
  })

  const open = (target: ExpenseCategory | 'new') => {
    save.reset()
    setEditing(target)
    form.setFieldsValue(
      target === 'new'
        ? { name: '', branchIds: [], active: true }
        : { name: target.name, branchIds: [...target.branchIds], active: target.active },
    )
  }

  const close = () => {
    save.reset()
    setEditing(null)
  }

  const columns: ColumnsType<ExpenseCategory> = [
    {
      key: 'name',
      title: t('expenseCategories.name'),
      render: (_, row) => seededName('accounts', row.code, row.name, t),
    },
    {
      key: 'code',
      title: t('expenseCategories.code'),
      width: 110,
      render: (_, row) => <Typography.Text code>{row.code}</Typography.Text>,
    },
    {
      key: 'branches',
      title: t('expenseCategories.branches'),
      render: (_, row) =>
        row.branchIds.length === 0 ? (
          <Typography.Text type="secondary">{t('expenseCategories.allBranches')}</Typography.Text>
        ) : (
          <Space size={[4, 4]} wrap>
            {row.branchIds.map((id) => (
              <Tag key={id}>{branchName(id)}</Tag>
            ))}
          </Space>
        ),
    },
    {
      key: 'state',
      title: t('expenseCategories.state'),
      width: 120,
      render: (_, row) =>
        row.active ? <Tag color="green">{t('expenseCategories.active')}</Tag> : <Tag>{t('expenseCategories.inactive')}</Tag>,
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        <RowActions items={[{ key: 'edit', label: t('common.edit'), icon: <EditOutlined />, onClick: () => open(row) }]} />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('nav.expenseCategories')}
        description={t('pageIntro.expenseCategories')}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => open('new')}>
            {t('expenseCategories.add')}
          </Button>
        }
      />

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('expenseCategories.rule')} />
        {categories.isError ? <Alert type="error" showIcon message={translateError(categories.error, t)} /> : null}
        {branches.isError ? <Alert type="warning" showIcon message={translateError(branches.error, t)} /> : null}
        <Table<ExpenseCategory>
          rowKey={(row) => row.id}
          loading={categories.isPending}
          dataSource={categories.data ?? []}
          columns={columns}
          pagination={false}
          scroll={{ x: 720 }}
          locale={{ emptyText: t('expenseCategories.empty') }}
        />
      </Space>

      <Modal
        open={editing !== null}
        title={editing === 'new' ? t('expenseCategories.addTitle') : t('expenseCategories.editTitle')}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        confirmLoading={save.isPending}
        onCancel={close}
        destroyOnHidden
        onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {save.isError ? <Alert type="error" showIcon message={translateError(save.error, t)} /> : null}
          <Form<CategoryForm> form={form} layout="vertical" requiredMark preserve={false}>
            <Form.Item
              name="name"
              label={t('expenseCategories.name')}
              rules={[{ required: true, whitespace: true, message: t('expenseCategories.nameRequired') }]}
            >
              <Input maxLength={EXPENSE_CATEGORY_NAME_MAX} />
            </Form.Item>
            <Form.Item name="branchIds" label={t('expenseCategories.branches')} extra={t('expenseCategories.branchesHint')}>
              <Select<string[]>
                mode="multiple"
                allowClear
                optionFilterProp="label"
                loading={branches.isPending}
                placeholder={t('expenseCategories.allBranches')}
                options={(branches.data ?? []).map((branch) => ({
                  value: branch.id,
                  label: `${branch.displayName} (${branch.code})`,
                }))}
              />
            </Form.Item>
            <Form.Item name="active" label={t('expenseCategories.active')} valuePropName="checked">
              <Switch />
            </Form.Item>
          </Form>
        </Space>
      </Modal>
    </>
  )
}
